import { Defaults } from 'es-decorators'
import { refundRequestUrl } from 'init/refund-request-url'
import { FormView } from './form-view'
import { Model } from 'es6/components/model'
import extend from 'lodash/extend'
import some from 'lodash/some'

/**
 * Представление отвечает за загрузку и дроп файлов.
 */
@Defaults({
  events: {
    'change .input_file': 'preparePhotoUpload',
    'click .js-cancel-upload': 'removeFile',
    'drop .upload-files': 'getDropFile',
    'dragover .upload-files': 'draggedIn',
    'dragenter .upload-files': 'draggedIn',
    'dragleave .upload-files': 'draggedOut',
    'dragend .upload-files': 'draggedOut',
    'click .submit-button': 'validate',
  },
  extendedUploadOptions: {
    urlRoot: refundRequestUrl,
    notUploadImmediately: true,
  },
  fileSizeLimit: 10485760, // 10Mb
  additionalOptions: ['additionalData'],
})
export class UploadFilesFormView extends FormView {
  /**
   * @inheritDoc
   */
  run () {
    this.uploadOptions = extend(this.uploadOptions, this.extendedUploadOptions)
    this.model = new Model()
    this.listenTo(this, 'file-prepared', this.successFileAdded)
    super.run()
  }

  /**
   * Подготовка данных при нативной загрузке файла в инпут
   * @param {Event} e - событие изменения инпута.
   */
  preparePhotoUpload (e) {
    this.activeUploadField = $(e.currentTarget).closest('.upload-files')
    let file = e.currentTarget.files[0]
    if (file && this._validateFile(file)) {
      this.addStateToActiveInput('processing-upload', file.name)
      this.setFileFieldOption(this.activeUploadField)
      super.photoUpload(e)
    }
  }

  /**
   * @inheritDoc
   */
  prepareToUpload (file, itemId) {
    if (!this._validateFile(file)) {
      return
    }

    const formData = this.formData
    const fileField = this.uploadOptions && this.uploadOptions.fileField
      ? this.uploadOptions.fileField
      : ''

    if (!fileField) {
      throw new Error('Field name for file upload is not defined.')
    }

    formData[fileField] = {
      file: file,
      fileName: file.name,
    }
  }

  /**
   * Валидация файла
   * @param {File} file Файл.
   * @returns {boolean} Файл корректный
   * @private
   */
  _validateFile (file) {
    const errorMessage = this.activeUploadField.next('.error-message')
    let isValidFile = true
    errorMessage.text('')
    this.activeUploadField.removeClass('error-upload')
    if (file.size > this.fileSizeLimit) {
      this.addStateToActiveInput('error-upload')
      errorMessage.text('Слишком тяжелый файл. Загрузите файл до 10MB.')
      isValidFile = false
    }
    if (file.type !== 'image/jpeg' && file.type !== 'image/png' && file.type !== 'application/pdf') {
      this.addStateToActiveInput('error-upload')
      errorMessage.text('Загрузите файл в формате JPG, PNG или PDF.')
      isValidFile = false
    }

    return isValidFile
  }

  /**
   * Удаление добавленной фотографии.
   * @param {Event} e Событие клика на кнопку удаления.
   */
  removeFile (e) {
    const $currentTarget = this.$(e.currentTarget)
    const $input = $currentTarget.parent()
    delete this.formData[($input.data('file-field'))]
    this.validateFileLength()
    $input.removeClass('success-upload')
    $input.find('.input-placeholder').removeClass('not_display')
    $input.find('.file-name-text').text('')
    $input.find('.input_file').val('')
  }

  /**
   * Функционал дропа файлов
   * @param {Event} e Событие дропа файла.
   */
  getDropFile (e) {
    e.preventDefault()
    e.stopPropagation()
    let droppedFile = e.originalEvent.dataTransfer.files[0]
    this.activeUploadField = this.$(e.currentTarget)
    if (this.activeUploadField.hasClass('processing-upload')
      || this.activeUploadField.hasClass('success-upload')) {
      return
    }

    this.draggedOut(e)
    if (this._validateFile(droppedFile)) {
      this.addStateToActiveInput('processing-upload', droppedFile.name)
      this.setFileFieldOption(this.activeUploadField)
      this.photoUpload(null, [droppedFile])
    }
  }

  /**
   * Добавляет визуальное состояние к полю ввода
   * @param {string} className Добавляемый класс.
   * @param {string} text Добавляемый текст.
   */
  addStateToActiveInput (className, text) {
    this.activeUploadField.addClass(className)
    if (text) {
      this.activeUploadField.find('.file-name-text').text(text)
      this.activeUploadField.find('.input-placeholder').addClass('not_display')
    }
  }

  /**
   * Установка опции для formData
   * @param {jQuery} $field Элемент поля.
   */
  setFileFieldOption ($field) {
    this.uploadOptions.fileField = $field.data('file-field')
  }

  /**
   * Метод реагирует на ивент наведения на зону дропа.
   * @param {Event} e Ивент наведения на зону дропа.
   */
  draggedIn (e) {
    e.preventDefault()
    e.stopPropagation()
    this.$(e.currentTarget).addClass('is-dragged-in')
  }

  /**
   * Метод реагирует на ивент ухода из зону дропа
   * @param {Event} e Ивент ухода из зону дропа.
   */
  draggedOut (e) {
    e.preventDefault()
    e.stopPropagation()
    this.$(e.currentTarget).removeClass('is-dragged-in')
  }

  /**
   * Успешное добавление файла
   */
  successFileAdded () {
    this.activeUploadField
      .removeClass('processing-upload')
      .addClass('success-upload')
    this.validateFileLength()
  }

  /**
   * Валидация формы.
   * @returns {boolean} Все необходимые файлы загружены.
   */
  validateFileLength () {
    let $inputs = this.$('.upload-files'),
      missedFile = some($inputs, function (input) {
        return !this.formData[this.$(input).data('file-field')]
      }.bind(this))
    if (!missedFile) {
      this.$('.submit-button').removeAttr('disabled')
      return true
    }
    this.$('.submit-button').attr('disabled', true)
    return false
  }

  /**
   * @inheritDoc
   * @returns {FormData} Данные формы.
   */
  getData () {
    const data = new FormData()
    for (let key in this.formData) {
      if (this.formData.hasOwnProperty(key)) {
        data.append(key, this.formData[key].file, this.formData[key].fileName)
      }
    }

    if (this.additionalData) {
      for (let additionalKey in this.additionalData) {
        if (this.additionalData.hasOwnProperty(additionalKey)) {
          data.append(additionalKey, this.additionalData[additionalKey])
        }
      }
    }

    const fieldsData = super.getData()
    for (let fieldsKey in fieldsData) {
      if (fieldsData.hasOwnProperty(fieldsKey)) {
        data.append(fieldsKey, fieldsData[fieldsKey])
      }
    }

    return data
  }

  /**
   * Валидация перед сохранением
   * @param {Event} e Событие клика по кнопке отправки формы.
   * @returns {Request} - запрос на загрузку файлов.
   */
  validate (e) {
    e.preventDefault()
    e.stopPropagation()
    if (!this.isSaving && this.validateFileLength()) {
      this.isSaving = true
      this.$('.submit-button').attr('disabled', true)
      return this.postFormData(this.getData())
    }
  }

  /**
   * Очистка собранных данных.
   */
  clearData () {
    this.formData = {}
  }

  /**
   * Успешная загрузка данных
   * @private
   */
  _uploadSuccess () {
    this.isSaving = false
    this.clearData()
    this.$('.upload-files').removeClass('success-upload')
    this.$('.input-placeholder').removeClass('not_display')
    this.$('.file-name-text').text('')
  }
}
